/**
 * The thirteen report types, and the five a request will actually produce.
 *
 * `wp_reports.report_type`'s CHECK accepts thirteen values — Master §19's mandatory
 * export set. **Five have a builder in this build**; the other eight are refused at
 * `POST /reports` with a `400` rather than accepted and generated empty, which is
 * Task 46's deliberate decision and the right one: an empty attendance file handed to
 * payroll is worse than a refusal.
 *
 * So the create form offers all thirteen and says, per option, which will produce a
 * file today. Two alternatives were considered and both are worse:
 *
 *  - **Offering only the five.** The other eight then look like they do not exist,
 *    when in fact they are specified, in the column's CHECK, and coming. A person who
 *    needs `website_usage` has to be told it is not built yet, not left to conclude
 *    the product never had it.
 *  - **Offering thirteen with no marking.** Eight of them answer `400` naming a
 *    report type the person picked from a list this screen drew. A form that teaches
 *    people its own options are unreliable is a form they stop reading.
 *
 * `catalog.test.ts` compares `MVP_REPORT_TYPES` here against `MVP_REPORT_TYPES` in
 * `src/modules/reports/types.ts`, parsed at test time, as a **set in both
 * directions** — so the sixth builder marks itself the day it lands, and a builder
 * removed on the server cannot leave a green "available" here. It compares the
 * thirteen against `ReportType`'s enum in `openapi.yaml` the same way.
 */

import type { ReportFormatName, ReportTypeName } from '../api/types';

/** `ReportType` of `openapi.yaml`, in enum order. */
export const REPORT_TYPES: readonly ReportTypeName[] = [
  'attendance',
  'shift_compliance',
  'active_idle',
  'app_usage',
  'website_usage',
  'task_timer',
  'estimate_vs_actual',
  'productivity',
  'workload',
  'usb_file_events',
  'alerts_incidents',
  'device_health',
  'audit_log',
];

/** `MVP_REPORT_TYPES` of `src/modules/reports/types.ts` — the five with a builder. */
export const MVP_REPORT_TYPES: readonly ReportTypeName[] = [
  'attendance',
  'active_idle',
  'app_usage',
  'task_timer',
  'productivity',
];

const MVP: ReadonlySet<string> = new Set<string>(MVP_REPORT_TYPES);

/** Will `POST /reports` generate a file for this type, or refuse it? */
export function hasBuilder(type: ReportTypeName): boolean {
  return MVP.has(type);
}

/**
 * `ReportFormat` — the three `wp_reports.format` accepts.
 *
 * `json` was on this enum until Task 46 removed it, because the CHECK constraint
 * refuses it and a format the constraint rejects is a `500` waiting for its first
 * caller. It is not listed here and must not come back without the column.
 */
export const REPORT_FORMATS: readonly ReportFormatName[] = ['csv', 'xlsx', 'pdf'];
